import { Button, Group, Switch as MantineSwitch, TextInput, Tooltip, Text } from "@mantine/core";
import { IDisciplin, IProjectDeprecated } from "@mctypes/global.types";
import {
  ICapitalLetter,
  IOtherAttributeConfigApi,
  IOtherAttributeConfigItemProps,
  IOtherAttributesConfigLean,
} from "@schemas/otherAttributesSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconCircle, IconCircleCheck, IconSettings } from "@tabler/icons-react";
import { ColDef, SideBarDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as R from "ramda";
import { useMemo, useState } from "react";
import { useHistory } from "react-router";
// ReactSwitch removed; using MantineSwitch for all toggles
import {
  useGetProjectMembersQuery,
  usePutProjectMemberMutation,
  usePutProjectMemberOtherAttributesMutation,
  usePutUserProfileMutation,
} from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { Avatar, FailedAlert, MCLoader } from "../../../../components";
import OtherAttributesConfig from "../../../../components/OtherAttributes/OtherAttributesConfig/OtherAttributesConfig";
import { CellEditFormatter } from "../../../../components/columnGlobal";
import { selectUserMember, setMemberIdModalInRedux } from "../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { MEMBER_GROUPS } from "../../../globalConstants";
import {
  useAddMemberOtherAttributeMutation,
  useGetProjectQuery,
  useUpdateMemberOtherAttributeMutation,
  useUpdateMemberOtherAttributesOrderMutation,
} from "../../../projectRTK";
import { getProjectIdFromUrl, sortArrayByString } from "../../../utils";
import { dateExportFormatter } from "../../issuesApp/IssuesTab/TableRefactor/issueTableFormatters";
import { DISCIPLINER } from "../../start/constants";
import { McSelect } from "../../../../components/McSelect/McSelect";
import ProjectGroupSelector from "./ProjectGroupSelector";

type MembersTableProps = {
  showSettings?: boolean;
};

const MembersTable: React.FC<MembersTableProps> = ({ showSettings = true }) => {
  const projectId = useAppSelector(selectCurrentProjectId);
  const { data: project, isLoading: projectLoading, isError: errorProject } = useGetProjectQuery(projectId);
  const {
    data: members = [],
    isLoading: membersLoading,
    isError: errorMembers,
  } = useGetProjectMembersQuery({ projectId }, { skip: project == null });

  const [updateUser, { isError: updateUserError }] = usePutUserProfileMutation();
  const [updateMemberAttribute, { isError: updateMemberError }] = usePutProjectMemberMutation();
  const [addMemberOtherAttribute] = useAddMemberOtherAttributeMutation();
  const [updateMemberOtherAttribute] = useUpdateMemberOtherAttributeMutation();
  const [updateMemberOtherAttributesOrder] = useUpdateMemberOtherAttributesOrderMutation();
  const [updateMemberOtherAttributesData] = usePutProjectMemberOtherAttributesMutation();

  const isError = errorProject || errorMembers || updateUserError || updateMemberError;
  const isLoading = projectLoading || membersLoading;

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.globalReducer.user);
  const loggedInUserMember: IProjectMemberApiItem = useAppSelector(selectUserMember);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [showAllMembers, setShowAllMembers] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>("");

  const getDisciplines = useMemo(() => {
    if (project == null) {
      return [];
    }
    const projectDisciplines = project.disciplines == null ? [] : project.disciplines;
    if (project.useDefaultDisciplines === true) {
      return [...DISCIPLINER, ...projectDisciplines];
    } else {
      return projectDisciplines;
    }
  }, [project]);

  const isUserMemberAdminOrInternal = (member: IProjectMemberApiItem) => {
    if (member == null) {
      return false;
    }
    return member.group === "ADMIN" || member.group === "INTERNAL";
  };

  const sideBar = useMemo(() => {
    const sb: SideBarDef = {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivotMode: true,
            // Other parameters...
          },
        },
        {
          id: "info",
          labelDefault: "Info",
          labelKey: "info",
          iconKey: "info",
        },
      ],
      position: "left",
      defaultToolPanel: localStorage.getItem("columnPanelSeen") === "true" ? "" : "columns",
    };
    return sb;
  }, []);

  const getAgGridOtherAttributeColumns = (
    otherAttributesConfig: IOtherAttributesConfigLean,
  ): ColDef<IProjectMemberApiItem>[] => {
    const otherAttributesEntries = Object.entries(otherAttributesConfig ?? {}).map(([key, value]) => ({
      key,
      ...value,
    }));
    const groupedBySection = R.groupBy((item) => item.section, otherAttributesEntries);

    const cols: ColDef<IProjectMemberApiItem>[] = [];
    Object.entries(groupedBySection).forEach(([sectionTitle, attributesInSection]) => {
      attributesInSection
        .sort((a, b) => a.order - b.order)
        .forEach((attr) => {
          if (!attr.isActive) return;

          let customParams: Partial<ColDef<IProjectMemberApiItem>> = {};
          switch (attr.typeOfValue) {
            case "MULTILINESTRING":
              customParams = {
                filter: "agTextColumnFilter",
                cellEditor: "agLargeTextCellEditor",
                cellEditorPopup: true,
                minWidth: 200,
                cellRenderer: (params) => CellEditFormatter(params.value),
                autoHeight: true,
              };
              break;
            case "NUMBER":
              customParams = {
                filter: "agNumberColumnFilter",
                cellEditor: "agNumberCellEditor",
                valueGetter: (params) => {
                  const val = params.data.memberOtherAttributesData?.[attr.key];
                  return typeof val === "number" ? val : null;
                },
                valueSetter: (params) => {
                  const num = Number(params.newValue);
                  if (isNaN(num)) return false;
                  params.data.memberOtherAttributesData = {
                    ...params.data.memberOtherAttributesData,
                    [attr.key]: num,
                  };
                  return true;
                },
              };
              break;
            case "BOOLEAN":
              customParams = {
                filter: false,
                editable: false,
                valueGetter: (params) => {
                  return params.data.memberOtherAttributesData?.[attr.key] ?? null; // true / false / null
                },
                cellRenderer: (params) => (
                  <div className="d-flex align-items-center h-100">
                    <MantineSwitch
                      disabled={attr.locked === true}
                      checked={params.value === true}
                      size="sm"
                      onChange={(e) =>
                        handleUpdateMemberAttribute({
                          data: params.data,
                          colDef: { field: `memberOtherAttributesData.${attr.key}` },
                          newValue: e.currentTarget.checked,
                        })
                      }
                    />
                  </div>
                ),
              };
              break;
            case "DROPDOWN":
              const options = attr.tableOptions ?? [];
              const OptionWithColor = (params: ValueFormatterParams) => {
                const values = Array.isArray(params.value) ? params.value : [params.value];
                return (
                  <div className="d-flex flex-wrap">
                    {values.map((value, i) => {
                      const found = options.find((o) => String(o.value) === String(value));
                      return (
                        <div key={i} className="d-flex align-items-center mr-2">
                          {found?.color && <div className="statusdot" style={{ backgroundColor: found.color }} />}
                          <div>{String(value ?? "")}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              };
              customParams = {
                filter: "agFilter",
                cellEditor: "agRichSelectCellEditor",
                editable: !attr.locked,
                cellEditorParams: {
                  values: options.map((o) => o.value),
                  cellRenderer: OptionWithColor,
                  valueListMaxHeight: 220,
                  multiSelect: attr.allowMultipleSelectionsInDropdown === true,
                },
                cellRenderer: OptionWithColor,
                valueSetter: (params) => {
                  params.data.memberOtherAttributesData = {
                    ...params.data.memberOtherAttributesData,
                    [attr.key]: params.newValue,
                  };
                  return true;
                },
              };
              break;
            case "DATE":
              customParams = {
                filter: "agDateColumnFilter",
                cellEditor: "agDateStringCellEditor",
                valueFormatter: dateExportFormatter,
                valueGetter: (params) => params.data.memberOtherAttributesData?.[attr.key] ?? "",
              };
              break;
            default:
              customParams = { filter: "agTextColumnFilter" };
          }

          cols.push({
            field: `memberOtherAttributesData.${attr.key}`,
            headerName: attr.label,
            autoHeaderHeight: true,
            editable: !attr.locked,
            ...customParams,
            maxWidth: 300,
            context: sectionTitle,
            cellClass: "overflow-hidden px-1",
          });
        });
    });
    return cols;
  };

  const otherAttributesColumns: ColDef<IProjectMemberApiItem>[] = useMemo(() => {
    if (!project?.memberOtherAttributesConfig) return [];

    return getAgGridOtherAttributeColumns(project.memberOtherAttributesConfig).map((col) => ({
      ...col,
      suppressColumnsToolPanel: false,
    }));
  }, [project]);

  const columns: ColDef<IProjectMemberApiItem>[] = useMemo(
    () => [
      { field: "_id", hide: true, suppressColumnsToolPanel: true },
      {
        field: "isPassive",
        headerName: "",
        maxWidth: 80,
        hide: editMode === false,
        suppressColumnsToolPanel: true,
        cellRenderer: (params) => {
          return (
            <div className="d-flex" style={{ transform: "scale(0.9)" }}>
              <MantineSwitch
                withThumbIndicator={false}
                size="md"
                checked={params.data.isPassive !== true}
                onChange={() => handleUpdateMemberAttribute(params)}
              />
            </div>
          );
        },
      },
      {
        field: "user.name",
        flex: 2,
        filter: "agTextColumnFilter",
        minWidth: 160,
        menuTabs: ["filterMenuTab"],
        headerName: "Navn",
        cellStyle: {
          whiteSpace: "normal",
          lineHeight: "1.5",
          wordBreak: "break-word",
        },
        cellRenderer: (params) => {
          return (
            <div
              className="d-flex pointer hoverText align-items-center"
              onClick={() => dispatch(setMemberIdModalInRedux(params.data._id))}
            >
              {params.data.user && <Avatar id={params.data.user._id} name={params.data.user.name} />}
              <div className="two-line-text-truncate ml-2">{params.value}</div>
              {params.data.isPassive && (
                <span className="badge badge-danger ml-2 my-1 d-flex align-items-center">Passiv</span>
              )}
            </div>
          );
        },
      },
      {
        field: "user.email",
        headerName: "Epost",
      },
      {
        field: "user.employeeNumber",
        headerName: "Ansattnr",
        hide: true,
        editable: false,
      },
      {
        field: "user.phoneNumber",
        headerName: "Tlf",
        editable: true,
        cellRenderer: (params) => {
          return (
            <span>
              {params.value}
              {!params.value && <i className="fa fa-pencil ml-2 " title="Rediger" />}
            </span>
          );
        },
      },
      {
        field: "user.domain",
        headerName: "Domene",
        filter: true,
      },
      {
        field: "user.location",
        headerName: "Lokasjon",
        cellClass: "grid-display-block",
        editable: true,
        filter: true,
      },
      {
        field: "group",
        headerName: "Gruppe",
        cellClass: "grid-display-block",
        autoHeight: true,
        filter: true,
        minWidth: 140,
        hide: isUserMemberAdminOrInternal(loggedInUserMember) === false,
        cellRenderer: (params) => {
          const cell = params.value;
          const row = params.data;
          const memberGroup = MEMBER_GROUPS.find((member) => member.value === cell);
          if ((loggedInUserMember && loggedInUserMember.group !== "ADMIN") || user.email === row.user.email) {
            return <Text c="dimmed">{memberGroup && memberGroup.label}</Text>;
          }
          return <ProjectGroupSelector memberId={row._id} group={cell} />;
        },
      },
      {
        field: "disciplin",
        headerName: "Disiplin",
        cellClass: "grid-display-block",
        minWidth: 160,
        autoHeight: true,
        filter: true,
        cellRenderer: (params) => {
          const cell = params.value;
          const row = params.data;
          return <DisciplineSelector memberId={row._id} disciplines={getDisciplines} discipline={cell} />;
        },
      },
      ...otherAttributesColumns,
    ],

    [editMode, project, otherAttributesColumns],
  );

  const handleUpdateMemberAttribute = async (params: any) => {
    const { data, colDef, newValue, node } = params;

    if (!data) return;

    // Make a clone to update immutably
    const newRowData = { ...data };

    // --- User attributes (PATCH-objekt) ---
    if (colDef.field?.startsWith("user.")) {
      const attr = colDef.field.split(".")[1] as "name" | "location" | "phoneNumber";
      const value = (newValue ?? "").toString().trim();

      await updateUser({
        body: {
          userId: data.user._id,
          patch: { [attr]: value }, // ⬅️ endast fältet som ändrats
        },
      });

      newRowData.user = { ...data.user, [attr]: value };
      node?.setData(newRowData);
      return;
    }

    // --- isPassive toggle ---
    if (colDef.field === "isPassive") {
      const newVal = data.isPassive === true ? false : true;
      await updateMemberAttribute({ body: { projectId, memberId: data._id, attribute: "isPassive", value: newVal } });
      newRowData.isPassive = newVal;
      node?.setData(newRowData);
      return;
    }

    // --- Other attributes ---
    if (colDef.field?.startsWith("memberOtherAttributesData.")) {
      const key = colDef.field.replace("memberOtherAttributesData.", "");
      let valueToSave: any = newValue;

      // BOOLEAN conversion (for toggle)
      if (newValue === "Ja") valueToSave = true;
      if (newValue === "Nei") valueToSave = false;

      // NUMBER conversion
      if (colDef.cellEditor === "agNumberCellEditor") {
        if (newValue === null || newValue === undefined || newValue === "") {
          valueToSave = null; // allow clearing number
        } else {
          const num = Number(newValue);
          if (isNaN(num)) return; // ignore invalid number
          valueToSave = num;
        }
      }

      // DATE conversion
      if (colDef.cellEditor === "agDateStringCellEditor" && newValue) {
        valueToSave = new Date(newValue).toISOString();
      }

      await updateMemberOtherAttributesData({
        body: { projectId, memberId: data._id, key, value: valueToSave },
      });

      newRowData.memberOtherAttributesData = {
        ...data.memberOtherAttributesData,
        [key]: valueToSave,
      };
      node?.setData(newRowData);
      return;
    }
  };

  const getRowStyle = (params) => {
    if (params.data?.isPassive === true) {
      return { opacity: "0.4" };
    }
  };

  const filteredMembers = useMemo(() => {
    if (members == null) {
      return [];
    }

    if (editMode === true) {
      return members;
    }

    if (showAllMembers === false) {
      return members.filter((member) => member.isPassive === false);
    }
    return members;
  }, [members, showAllMembers, editMode]);

  return (
    <>
      {isError && <FailedAlert />}
      {isLoading && <MCLoader />}
      {showSettings && (
        <TableActionBar
          showAllMembers={showAllMembers}
          setShowAllMembers={setShowAllMembers}
          setEditMode={setEditMode}
          editMode={editMode}
          setFilterText={setFilterText}
          isAdmin={loggedInUserMember?.group === "ADMIN"}
          project={project}
          onAdd={(item) =>
            addMemberOtherAttribute({
              projectId,
              body: { memberOtherAttributesConfigItemProps: item },
            })
          }
          onEdit={(item, key) =>
            updateMemberOtherAttribute({
              projectId,
              body: { key, memberOtherAttributesConfigItemProps: item },
            })
          }
          onUpdateOrder={(idsInOrder) => updateMemberOtherAttributesOrder({ projectId, idsInOrder })}
        />
      )}
      <div className="ag-theme-quartz w-100 h-100" id="myGrid">
        <AgGridReact
          defaultColDef={{
            suppressHeaderMenuButton: true,
            autoHeight: false,
            minWidth: 100,
            flex: 1,
          }}
          rowData={filteredMembers}
          getRowId={(p) => p.data._id}
          cellSelection={true}
          columnDefs={columns}
          readOnlyEdit={true}
          suppressContextMenu={true}
          localeText={AG_GRID_LOCALE_NOR}
          pagination={true}
          quickFilterText={filterText}
          paginationPageSize={50}
          getRowStyle={getRowStyle}
          rowBuffer={50}
          onCellEditRequest={handleUpdateMemberAttribute}
          sideBar={sideBar}
        />
      </div>
    </>
  );
};

export default MembersTable;

const DisciplineSelector: React.FC<{ discipline?: string; disciplines: IDisciplin[]; memberId: string }> = ({
  discipline,
  disciplines,
  memberId,
}) => {
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const [updateMemberAttribute] = usePutProjectMemberMutation();

  const options = useMemo(() => {
    const sorted = sortArrayByString(disciplines, "disc.short").map((disc) => ({
      value: disc.short,
      label: disc.short,
      fullLabel: `${disc.short} - ${disc.long}`,
    }));

    // Add clear option at the top if a value is selected
    if (discipline) {
      return [{ value: "", label: "Fjern valg", fullLabel: "Fjern valg" }, ...sorted];
    }
    return sorted;
  }, [disciplines, discipline]);

  return (
    <McSelect
      w="100%"
      variant="unstyled"
      styles={{
        input: {
          background: "transparent",
          border: "none",
          boxShadow: "none",
          paddingInline: 0,
        },
      }}
      data={options}
      value={discipline ?? null}
      placeholder="Ikke satt"
      renderOption={({ option }) => (option as { fullLabel?: string }).fullLabel ?? option.label}
      dropdownWidth={220}
      onChange={(value) => {
        if (value === discipline) {
          return;
        }

        updateMemberAttribute({
          body: {
            projectId,
            attribute: "disciplin",
            value: value ?? "",
            memberId,
          },
        });
      }}
    />
  );
};

const TableActionBar: React.FC<{
  showAllMembers: boolean;
  setShowAllMembers: (bool: boolean) => void;
  setFilterText: (text: string) => void;
  setEditMode: (bool: boolean) => void;
  isAdmin: boolean;
  editMode: boolean;
  project: IProjectDeprecated;
  onAdd: (item: IOtherAttributeConfigItemProps) => void;
  onEdit: (item: IOtherAttributeConfigItemProps, key: ICapitalLetter) => void;
  onUpdateOrder: (idsInOrder: ICapitalLetter[]) => void;
}> = ({
  showAllMembers,
  setShowAllMembers,
  setFilterText,
  setEditMode,
  editMode,
  isAdmin,
  project,
  onAdd,
  onEdit,
  onUpdateOrder,
}) => {
  return (
    <>
      {editMode && (
        <div className="w-100 mb-2">
          <h4>Egne attributer</h4>
          <OtherAttributesConfig
            otherAttributesConfig={(project?.memberOtherAttributesConfig as IOtherAttributeConfigApi) ?? {}}
            onAdd={onAdd}
            onEdit={onEdit}
            onUpdateOrder={onUpdateOrder}
            taskOrIssue={"TASK"}
          />
        </div>
      )}
      <Group gap="xs" p={4} wrap="nowrap" style={{ alignItems: "stretch" }}>
        <TextInput
          placeholder="Søk"
          onChange={(e) => setFilterText(e.currentTarget.value)}
          style={{ maxWidth: 300 }}
          maw={300}
        />
        <Tooltip
          multiline
          w={220}
          openDelay={500}
          label="Passive medlemmer fjernes fra prosjektet i MCApps, men ikke i Maconomy."
          withArrow
        >
          <Button
            variant="secondary-filled"
            onClick={() => setShowAllMembers(!showAllMembers)}
            leftSection={
              <>{showAllMembers ? <IconCircleCheck scale={1.8} color="#379964" /> : <IconCircle scale={1.8} />}</>
            }
          >
            Vis passiv
          </Button>
        </Tooltip>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <Button variant="outline" onClick={() => setEditMode(!editMode)} rightSection={<IconSettings size={16} />}>
            Innstillinger
          </Button>
        )}
        {/* <ExcelExport
          columns={columns.filter((col, index) => index > 1)}
          data={memberFormatted}
          filePrefix="Prosjektmedlemmer"
          isAgGrid={true}
        /> */}
      </Group>
    </>
  );
};
